/**
 * Kane Launch — SP-initiated SSO entry point (#385).
 *
 * Kane OneDeck sends the user here to start a login with their ProMedic
 * account (e.g. from OneDeck's PWA login on a phone, where there is no local
 * user/password):
 *
 *   OneDeck "Entrar con ProMedic"
 *     → {PROMEDIC_URL}/kane-launch?return_to={ONEDECK_URL}/sso
 *     → (this route) mint handoff token, redirect to  return_to#token={token}
 *     → OneDeck /sso (SsoHandoff.tsx) exchanges the token and logs the user in.
 *
 * This mirrors the popup-based `openKaneAssistant` in PrivateLayout.js, but
 * driven by OneDeck instead of a ProMedic button, and redirecting the current
 * window (SP-initiated) instead of a popup.
 *
 * Security: `return_to` is validated against an allowlist (kaneReturnTo.js)
 * before any token is minted or forwarded. The token always rides in the URL
 * fragment (#), never in a query string or log. If there is no ProMedic
 * session, we detour through /login and resume the flow afterwards.
 *
 * @module pages/KaneLaunch
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getKaneHandoffToken } from '../services/kaneAuth';
import { validateReturnTo } from '../utils/kaneReturnTo';

// Survives the /login detour: Login.js restores only the pathname of the
// route the user was headed to (dropping the query string), so we stash the
// validated return_to here and read it back on resume.
const RETURN_TO_STORAGE_KEY = 'kane_launch_return_to';

const KaneLaunch = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  // Guard against double-execution (React StrictMode / re-renders) so the
  // token is minted and the redirect fired at most once.
  const startedRef = useRef(false);

  // Resolve return_to: query param first, sessionStorage fallback (set right
  // before the login detour). Re-validate in all cases — never trust storage.
  const rawReturnTo =
    new URLSearchParams(location.search).get('return_to') ||
    (typeof window !== 'undefined'
      ? window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY)
      : null);
  const validatedReturnTo = validateReturnTo(rawReturnTo);

  useEffect(() => {
    // Wait for the auth state to settle before deciding anything.
    if (loading) return;
    if (startedRef.current) return;

    // Invalid / missing / non-allowlisted return_to → reject. No token minted.
    if (!validatedReturnTo) {
      startedRef.current = true;
      setError('El destino de retorno no es válido. No se puede completar el inicio de sesión con Kane.');
      return;
    }

    // No ProMedic session → stash the (validated) destination and detour
    // through login, resuming this route once signed in.
    if (!isAuthenticated) {
      window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, validatedReturnTo);
      navigate('/login', {
        replace: true,
        state: { from: { pathname: '/kane-launch' } },
      });
      return;
    }

    // Authenticated → mint the handoff token and hand control to OneDeck.
    startedRef.current = true;
    (async () => {
      try {
        const { token } = await getKaneHandoffToken();
        window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
        // Fragment (#) keeps the JWT out of access logs / referer headers.
        window.location.replace(
          `${validatedReturnTo}#token=${encodeURIComponent(token)}`
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Kane handoff failed:', err?.message);
        window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
        setError('No se pudo generar el acceso a Kane. Intenta de nuevo desde ProMedic.');
      }
    })();
  }, [loading, isAuthenticated, validatedReturnTo, navigate]);

  if (error) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 p-4 text-center">
        <i className="bi bi-shield-exclamation text-danger" style={{ fontSize: '2.5rem' }} aria-hidden="true"></i>
        <h1 className="h5 mt-3 mb-2">No se pudo continuar con Kane</h1>
        <p className="text-muted" style={{ maxWidth: 420 }}>{error}</p>
        <a href="/dashboard" className="btn btn-primary mt-2">Ir a ProMedic</a>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column justify-content-center align-items-center min-vh-100">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Conectando con Kane...</span>
      </div>
      <p className="text-muted mt-3">Conectando con Kane...</p>
    </div>
  );
};

export default KaneLaunch;
